import { useEffect, useMemo, useState } from 'react';
import { useWindowDimensions, useQuery as useQueryString } from '@kiper/hooks';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoKiper from '../../assets/images/logo-kiper.svg';
import logoPorter from '../../assets/images/logo-porter.svg';
import activateKeyKiperImg from '../../assets/images/activate-key-kiper.svg';
import activateKeyPorterImg from '../../assets/images/activate-key-porter.svg';
import activateKeyWhiteLabelImg from '../../assets/images/activate-key-white-label.svg';
import './styles.css';

const brandTypes = {
  PORTER: 'p',
  KIPER: 'k',
  WHITELABEL: 'w',
};

const brandSettings = {
  [brandTypes.PORTER]: {
    brandLogoSrc: logoPorter,
    contentImgSrc: activateKeyPorterImg,
    appleId: 'porter/id1579435806',
    packageName: 'br.com.portergroup.appporter',
    brandName: 'porter',
  },
  [brandTypes.KIPER]: {
    brandLogoSrc: logoKiper,
    contentImgSrc: activateKeyKiperImg,
    appleId: 'kiper/id1090915622',
    packageName: 'br.com.segware.kiper.mobile',
    brandName: 'kiper',
  },
  [brandTypes.WHITELABEL]: {
    brandLogoSrc: logoPorter,
    contentImgSrc: activateKeyWhiteLabelImg,
    brandName: 'porter',
  },
};

const getBrandSettingFromUrl = (brandSlug, queryStringParams) => {
  const settingsFromUrl = brandSettings[brandSlug];
  if (brandSlug === brandTypes.WHITELABEL) {
    settingsFromUrl.appleId = `${queryStringParams.get(
      'schema',
    )}/id${queryStringParams.get('appleId')}`;
    settingsFromUrl.packageName = queryStringParams.get('packageName');

    /* provisório: workaround para resolver problema do pre-cadastro nas white-labels */
    // settingsFromUrl.brandName = queryStringParams.get('schema');
    /*  */
  }
  return settingsFromUrl;
};

const Spinner = () => (
  <div className="spinner">
    <div />
    <div />
    <div />
    <div />
  </div>
);

export default function ActivateKeyPage() {
  const { t } = useTranslation('activate-keys');
  const { brandSlug } = useParams();
  const queryStringParams = useQueryString();
  const { width: windowsWidth, height } = useWindowDimensions();
  const [brandSettingFromUrl, setBrandSettingFromUrl] = useState(null);
  const [loadingRedirect, setLoadingRedirect] = useState(false);

  useEffect(() => {
    if (!brandSlug || !(brandSlug in brandSettings)) window.location = '/';
    else {
      const settingsFromUrl = getBrandSettingFromUrl(
        brandSlug,
        queryStringParams,
      );
      setBrandSettingFromUrl(settingsFromUrl);
    }
  }, [brandSlug]);

  const isIOS = useMemo(() => {
    const userAgent =
      navigator?.userAgent || navigator?.vendor || window?.opera;
    return /iPad|iPhone|iPod/i.test(userAgent);
  }, [navigator]);

  const isDesktop = useMemo(() => windowsWidth > 1024, [windowsWidth]);

  const onDeeplinkRedirect = () => {
    if (isIOS) {
      setLoadingRedirect(true);

      const isSafari =
        /^((?!chrome|android|crios|fxios).)*safari/i.test(
          navigator.userAgent,
        ) && /iP(ad|hone|od)/i.test(navigator.userAgent);

      window.location = `${
        brandSettingFromUrl?.brandName
      }://?${queryStringParams.toString()}`;

      const timeout = setTimeout(() => {
        const isStillVisible = document.visibilityState === 'visible';

        if (isStillVisible) {
          if (isSafari) {
            const confirmRedirect = window.confirm(t('store-redirect-message'));
            if (confirmRedirect)
              window.location = `https://itunes.apple.com/app/${brandSettingFromUrl?.appleId}`;
          } else
            window.location = `https://itunes.apple.com/app/${brandSettingFromUrl?.appleId}`;
        }

        setLoadingRedirect(false);
      }, 1000);

      window.addEventListener('pagehide', () => clearTimeout(timeout));
    }
  };

  if (!brandSlug || !brandSettingFromUrl) return null;

  const brandSlugName = brandSlug === brandTypes.PORTER ? 'Porter' : 'Kiper';

  const androidIntentLink = `intent://?${queryStringParams.toString()}#Intent;scheme=${
    brandSettingFromUrl?.brandName
  };package=${
    brandSettingFromUrl?.packageName
  };S.browser_fallback_url=https://play.google.com/store/apps/details?id=${
    brandSettingFromUrl?.packageName
  }&referrer=${encodeURIComponent(window.location.href)};end`;

  return (
    <main style={{ height }} className="body-page">
      {isDesktop ? (
        <span>{t('message-fallback-desktop')}</span>
      ) : (
        <>
          {brandSlug !== brandTypes.WHITELABEL && (
            <img
              className="brand-logo"
              src={brandSettingFromUrl?.brandLogoSrc}
              alt="porter"
            />
          )}
          <img
            className="activate-key-brand-image"
            src={brandSettingFromUrl?.contentImgSrc}
            alt="porter"
          />
          <div className="download-area">
            <span className="activate-key-message">
              {t('redirect-message', {
                brand: brandSlug !== brandTypes.WHITELABEL ? brandSlugName : '',
              })}
            </span>

            {isIOS ? (
              <button
                type="button"
                className={`download-button background-color-${brandSlug}`}
                onClick={onDeeplinkRedirect}
                disabled={loadingRedirect}
              >
                {loadingRedirect ? <Spinner /> : t('redirect-button')}
              </button>
            ) : (
              <a
                href={androidIntentLink}
                className={`download-button background-color-${brandSlug}`}
              >
                {t('redirect-button')}
              </a>
            )}
          </div>
        </>
      )}
    </main>
  );
}
